import type React from "react";
import { useCallback } from "react";
import { useLink } from "solito/link";
import { Badge, CalendarText, ListItem, Paragraph, Paragraph2, Pill, Pip, styled, XStack, YStack } from "@medbillai/ui";
import { issueUrl } from "@medbillai/utils/urls";
import { analyticsService } from "../../../lib/analytics";
import type { CaseProps, Issue, IssueStatus } from "../utils/types";
import { cleanMarkdownText, formatEvent, getChatPillStyle, getIssueStatus, getTextColor } from "../utils/utils";
const getIssueBadge = (status: IssueStatus): React.ReactNode => {
  switch (status) {
    case "NEW_ACTIVITY":
      return <Pip />;
    case "CLOSED":
      return <Badge status="negative">Closed</Badge>;
    case "PROCESSING":
      return undefined;
  }
};
export const CaseListItem = ({
  issue,
  selected,
  markKeepOpen,
  source
}: {
  issue: Issue;
  selected: boolean;
  markKeepOpen?: () => void;
  source: string;
}) => {
  const status = getIssueStatus({
    closedAt: issue.closedAt,
    firstUnseenEventId: issue.firstUnseenEvent?.id
  });
  const lastEvent = issue.lastEvent;
  const eventContent = formatEvent(issue.firstUnseenEvent || lastEvent);
  const handlePress = useCallback(() => {
    analyticsService.trackEvent("OpenedIssue", {
      id: issue.id,
      source
    });
    markKeepOpen?.();
  }, [issue.id, source, markKeepOpen]);
  return <ListItem title={issue.title || ""} subtitle={cleanMarkdownText(eventContent)} badge={getIssueBadge(status)} metadataDate={lastEvent?.sentAt} href={issueUrl({
    issueId: issue.id,
    unread: status === "NEW_ACTIVITY",
    title: issue.title
  })} onPress={handlePress} selected={selected} data-sentry-element="ListItem" data-sentry-component="CaseListItem" data-sentry-source-file="CaseListItem.tsx" />;
};
const DateTextStyle = styled(Paragraph2, {
  whiteSpace: "nowrap",
  textAlign: "left"
});
export const CaseListItemLegacy = ({
  issue,
  borderHide = false,
  flashListView = false,
  selected,
  webHomePage = false
}: CaseProps) => {
  const status = getIssueStatus({
    closedAt: issue.closedAt,
    firstUnseenEventId: issue.firstUnseenEvent?.id
  });
  const unread = status === "NEW_ACTIVITY";
  const lastEvent = issue.lastEvent;

  // First check if firstUnseenEvent is available,
  // if available display that event formatted,
  // otherwise display most recent event formatted
  const eventContent = formatEvent(issue.firstUnseenEvent || lastEvent);
  return <YStack {...useLink({
    href: issueUrl({
      issueId: issue.id,
      unread,
      title: issue.title
    })
  })} px="$4" cur="pointer" pressStyle={{
    backgroundColor: "$backgroundPress"
  }} $platform-native={{
    f: 1
  }} backgroundColor={selected ? "$backgroundPress" : "$background0"} hoverStyle={{
    backgroundColor: "$backgroundPress"
  }} br={webHomePage ? "$4" : "$0"} data-sentry-element="YStack" data-sentry-component="CaseListItemLegacy" data-sentry-source-file="CaseListItem.tsx">
      <YStack ai="center" jc="flex-start" gap="$2" py="$4" flex={1} borderTopWidth={borderHide ? 0 : 0.5} borderBottomWidth={flashListView ? 0.5 : 0} borderColor="$strokeSecondary" backgroundColor="transparent" data-sentry-element="YStack" data-sentry-source-file="CaseListItem.tsx">
        <XStack gap="$5" ai="center" jc="space-between" f={1} width="100%" data-sentry-element="XStack" data-sentry-source-file="CaseListItem.tsx">
          <Paragraph f={1} numberOfLines={1} bold={unread} data-sentry-element="Paragraph" data-sentry-source-file="CaseListItem.tsx">
            {issue.title}
          </Paragraph>
          <Pill {...getChatPillStyle(status)} data-sentry-element="Pill" data-sentry-source-file="CaseListItem.tsx" />
        </XStack>
        <XStack gap="$5" ai="center" jc="space-between" f={1} width="100%" data-sentry-element="XStack" data-sentry-source-file="CaseListItem.tsx">
          <Paragraph2 f={1} numberOfLines={1} overflow="hidden" textOverflow="ellipses" bold={unread} color={getTextColor(status)}
        // For some reason, the text overflow is not working on web, so we need to set the style manually
        style={{
          textOverflow: "ellipsis"
        }} data-sentry-element="Paragraph2" data-sentry-source-file="CaseListItem.tsx">
            {cleanMarkdownText(eventContent)}
          </Paragraph2>
          <CalendarText date={lastEvent?.sentAt || ""} element={props => <DateTextStyle {...props} color={getTextColor(status)} bold={unread} />} data-sentry-element="CalendarText" data-sentry-source-file="CaseListItem.tsx" />
        </XStack>
      </YStack>
    </YStack>;
};